/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { HouseRounded, AddLocation } from "@material-ui/icons";
import ReactMapGL, {
    Marker,
    Popup,
    GeolocateControl,
    NavigationControl,
    MapLoadEvent,
} from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "react-map-gl-geocoder/dist/mapbox-gl-geocoder.css";
import Geocoder from "react-map-gl-geocoder";
import { makeStyles, createStyles } from "@material-ui/styles";
import { serverConfig } from "../../../utils/config";
import { PlaceInfo } from "./components/PlaceInfo";
import { SearchMarker } from "./components/SearchMarker";
import mapboxgl, { LngLatBounds } from "mapbox-gl";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) =>
    createStyles({
        container: {
            display: "flex",
            alignItems: "center",
            zIndex: 1,
            boxShadow: "0 30px 40px 0 rgb(16 36 94 / 20%)",
        },
        map: {
            // marginTop: 10
        },
        geocoder: {
            // margin: 10
        },
        pin: {
            color: "#FFFCF0",
            backgroundColor: "#EA7606",
            padding: "2px",
            border: "solid #FFFCF0 1px",
            borderRadius: "10px",
            transform: "scale3d(1.5, 1.5, 1)",
            zIndex: 1,
        },
        circle: {
            color: "#890608",
            transition: "transform 0.15s ease-in-out",
            "&:hover": {
                transform: "scale3d(1.5, 1.5, 1)",
                color: "#EA7606",
            },
            zIndex: 1,
        },
        popup: {
            zIndex: 3,
        },
    })
);

const mapboxApiKey = serverConfig.MapBoxApikey;

export interface IMapProps {
    height?: string | number;
    width?: string | number;
    alignStart?: boolean;
    setLocation?: boolean;
    updateLocation?: boolean;
    autoGeolocate?: boolean;
    setPin?: boolean;
    startLongitude?: number;
    startLatitude?: number;
    oldLongitude?: number;
    oldLatitude?: number;
    startZoom?: number;
    handleChange?: any;
    setMarkers?: boolean;
    markers?: any;
    boundaries?: any;
    onBoundariesChange?: any;
    isSearchingOnMap?: boolean;
    handleNext?: () => void;
}

export function MapView(props: IMapProps) {
    const [viewport, setViewport] = React.useState({
        longitude: props.startLongitude ?? 19.13697660832946,
        latitude: props.startLatitude ?? -1.2138498098141102,
        zoom: props.startZoom ?? 2,
    });

    const [pins, setPins] = React.useState([]);

    const [currentPlaceId, setCurrentPlaceId] = React.useState(null);

    const [isOnhover, setIsOnHover] = React.useState(null);

    const [showPins, setShowPins] = React.useState(false);

    const [showPrice, setShowPrice] = React.useState(false);

    const [boundary, setBoundary] = React.useState<LngLatBounds>();

    const handleMarkerClick = (id: any) => {
        setCurrentPlaceId(id);
    };

    const handleHoverOn = (id: any) => {
        setIsOnHover(id);
        setShowPrice(true);
    };

    const handleHoverOff = () => {
        setIsOnHover(null);
        setShowPrice(false);
    };

    const handleViewportChange = React.useCallback((newViewport) => {
        setViewport(newViewport);
        if (props.setLocation && props.handleChange) {
            props.handleChange(newViewport.longitude, newViewport.latitude);
        }
    }, []);

    const handleGeocoderViewportChange = React.useCallback((newViewport) => {
        const geocoderDefaultOverrides = { transitionDuration: 1000 };
        return handleViewportChange({
            ...newViewport,
            ...geocoderDefaultOverrides,
        });
    }, []);

    const { t } = useTranslation();

    React.useEffect(() => {
        if (props.setMarkers) {
            if (props.markers.length > 0) {
                setShowPins(true);
                setPins(props.markers);
            }
        }
    }, [props.markers]);

    React.useEffect(() => {
        const bounds = map.current?.getBounds();
        if (bounds) { // Ensure bounds is not null
            setBoundary(bounds);
        }
    }, [viewport]);

    React.useEffect(() => {
        if (boundary && props.isSearchingOnMap) {
            props.onBoundariesChange({
                East: boundary.getEast(),
                West: boundary.getWest(),
                North: boundary.getNorth(),
                South: boundary.getSouth(),
                Center: boundary.getCenter(),
                Zoom: map.current?.getZoom(),
            });
        }
    }, [boundary]);

    const handleRecenter = (
        longitude: number,
        latitude: number,
        zoom: number,
        transitionDuration: number
    ) => {
        const center = {
            longitude,
            latitude,
            zoom,
            transitionDuration,
        };
        handleViewportChange(center);
    };

    React.useEffect(() => {
        if (
            props.startLongitude &&
            props.startLatitude &&
            props.startZoom &&
            props.setMarkers === true
        ) {
            handleRecenter(
                props.startLongitude,
                props.startLatitude,
                props.startZoom,
                500
            );
        }
    }, [props.setMarkers, props.startLongitude, props.startLatitude]);

    const mapRef = React.useRef<any>(null);

    const map = React.useRef<mapboxgl.Map>();

    const handleCloseButton = () => {
        setCurrentPlaceId(null);
    };

    const handleLoad = (event: MapLoadEvent) => {
        const { target } = event;
        map.current = target;
        //console.log("Map loaded:", map.current.getStyle()); // Log the map style
        const bounds = map.current.getBounds();
        if (bounds) { // Ensure bounds is not null
            setBoundary(bounds);
        }
    };

    const classes = useStyles();

    return (
        <div
            className={classes.container}
            style={{ alignItems: props.alignStart ? "start" : "center" }}
        >
            <ReactMapGL
                ref={mapRef}
                mapboxApiAccessToken={mapboxApiKey}
                mapStyle="mapbox://styles/mapbox/streets-v11"//"mapbox://styles/santatra024/ckt8erdte4vy317mw1v2yyzu7"//"mapbox://styles/christepo/cm7wey59q014v01sbf5ca1j9o"  
                {...viewport}
                height={props.height ?? 500}
                width={props.width ?? "100%"}
                onViewportChange={handleViewportChange}
                className={classes.map}
                maxZoom={props.setPin || props.setMarkers ? 14 : 20}
                minZoom={props.setPin ? 4.5 : 0}
                onLoad={handleLoad}
            >
                <Geocoder
                    mapRef={mapRef}
                    mapboxApiAccessToken={mapboxApiKey}
                    onViewportChange={handleGeocoderViewportChange}
                    position="top-left"
                    collapsed={true}
                    clearAndBlurOnEsc={true}
                    reverseGeocode={true}
                    marker={false}
                />
                {props.setLocation && (
                    <Marker
                        longitude={viewport.longitude}
                        latitude={viewport.latitude}
                    >
                        <div
                            className={classes.circle}
                            onClick={props.handleNext}
                        >
                            <div>
                                <label> {t("label.marquer-ici")} </label>
                            </div>
                            <div>
                                <AddLocation />
                            </div>
                        </div>
                    </Marker>
                )}
                {props.updateLocation && (
                    <Marker
                        longitude={props.oldLongitude ?? 0}
                        latitude={props.oldLatitude ?? 0}
                    >
                        <div
                            className={classes.circle}
                            onClick={() =>
                                handleRecenter(
                                    props.oldLongitude ?? 0,
                                    props.oldLatitude ?? 0,
                                    props.startZoom ?? 13,
                                    500
                                )
                            }
                        >
                            <HouseRounded fontSize={"large"} />
                        </div>
                    </Marker>
                )}
                {props.setPin && (
                    <Marker
                        longitude={props.startLongitude ?? 0}
                        latitude={props.startLatitude ?? 0}
                    >
                        <div
                            className={classes.circle}
                            onClick={() =>
                                handleRecenter(
                                    props.startLongitude ?? 0,
                                    props.startLatitude ?? 0,
                                    props.startZoom ?? 13,
                                    500
                                )
                            }
                        >
                            <HouseRounded fontSize={"large"} />
                        </div>
                    </Marker>
                )}
                {props.setMarkers &&
                    showPins &&
                    pins.map((p: any) => (
                        <div>
                            <SearchMarker
                                p={p}
                                handleMarkerClick={handleMarkerClick}
                                handleHoverOn={handleHoverOn}
                                handleHoverOff={handleHoverOff}
                                showPrice={showPrice}
                                isOnhover={isOnhover}
                                viewport={viewport}
                            />
                            {p._id === currentPlaceId && (
                                <Popup
                                    offsetLeft={15}
                                    offsetTop={10}
                                    longitude={
                                        p.propertyData[0].location.longitude
                                    }
                                    latitude={
                                        p.propertyData[0].location.latitude
                                    }
                                    anchor="bottom"
                                    closeButton={false}
                                    onClose={() => {
                                        setCurrentPlaceId(null);
                                    }}
                                    className={classes.popup}
                                >
                                    <PlaceInfo
                                        datas={p}
                                        handleCloseButton={handleCloseButton}
                                    />
                                </Popup>
                            )}
                        </div>
                    ))}
                <GeolocateControl
                    style={{ right: 10, top: 10 }}
                    positionOptions={{ enableHighAccuracy: true }}
                    trackUserLocation={true}
                    auto={props.autoGeolocate}
                />
                <NavigationControl style={{ right: 10, bottom: 25 }} />
            </ReactMapGL>
        </div>
    );
}
